import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { PublicIncident } from '../types.ts';

interface Props {
  incidents: PublicIncident[];
}

const fmtDate = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const fmtDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const TIPO_LABEL: Record<string, string> = {
  FALHA: 'Falha',
  DEGRADACAO: 'Degradação',
};

function calcDuration(from: string, to: string): string {
  const diff = new Date(to).getTime() - new Date(from).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
}

const INITIAL_SHOW = 10;

export function IncidentHistory({ incidents }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<Set<number>>(new Set());

  if (incidents.length === 0) return null;

  const visible = showAll ? incidents : incidents.slice(0, INITIAL_SHOW);

  function toggleExpand(idx: number) {
    setExpandedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
      <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
        Histórico de Incidentes
      </h2>

      <div className="space-y-2">
        {visible.map((inc, idx) => {
          const isOpen = expandedIdx.has(idx);
          return (
            <div key={idx} className="rounded-lg border border-gray-100 dark:border-gray-800">
              <button
                onClick={() => toggleExpand(idx)}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm"
              >
                {isOpen
                  ? <ChevronDown size={14} className="shrink-0 text-gray-400" />
                  : <ChevronRight size={14} className="shrink-0 text-gray-400" />}
                <span className="flex-1 font-medium text-gray-900 dark:text-white">
                  {inc.titulo}
                </span>
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
                  style={{
                    backgroundColor: inc.tipo === 'FALHA' ? 'var(--color-status-down)' : 'var(--color-status-degraded)',
                  }}
                >
                  {TIPO_LABEL[inc.tipo] ?? inc.tipo}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {fmtDate.format(new Date(inc.iniciadoEm))}
                </span>
              </button>

              {isOpen && (
                <div className="border-t border-gray-100 px-4 py-3 text-sm dark:border-gray-800">
                  <div className="space-y-1.5 text-gray-600 dark:text-gray-400">
                    <p>
                      <span className="font-medium text-gray-900 dark:text-gray-300">Início:</span>{' '}
                      {fmtDateTime.format(new Date(inc.iniciadoEm))}
                    </p>
                    {inc.encerradoEm && (
                      <>
                        <p>
                          <span className="font-medium text-gray-900 dark:text-gray-300">Fim:</span>{' '}
                          {fmtDateTime.format(new Date(inc.encerradoEm))}
                        </p>
                        <p>
                          <span className="font-medium text-gray-900 dark:text-gray-300">Duração:</span>{' '}
                          {calcDuration(inc.iniciadoEm, inc.encerradoEm)}
                        </p>
                      </>
                    )}
                    {inc.descricao && (
                      <p>
                        <span className="font-medium text-gray-900 dark:text-gray-300">Descrição:</span>{' '}
                        {inc.descricao}
                      </p>
                    )}
                    {inc.causaRaiz && (
                      <p>
                        <span className="font-medium text-gray-900 dark:text-gray-300">Causa raiz:</span>{' '}
                        {inc.causaRaiz}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showAll && incidents.length > INITIAL_SHOW && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Ver todos ({incidents.length})
        </button>
      )}
    </div>
  );
}
